/**
 * Type definitions for the Test Suite Configuration page.
 *
 * This module defines all interfaces, types, and the discriminated-union
 * action type used by the Config Generator form.
 */

import { ToolRevisionData } from 'src/store/interfaces/Tool.types';

// ─── Schema Summary ────────────────────────────────────────────────────

export interface ParameterSummary {
  name: string;
  type: string;
  required: boolean;
  default: unknown;
  enum: string[] | null;
  examples: unknown[] | null;
}

export interface SchemaSummary {
  tool_name: string;
  parameters: Record<string, ParameterSummary>;
  required_params: string[];
}

// ─── Template Definitions ──────────────────────────────────────────────

export type TemplateCategory =
  | 'basic'
  | 'scenario'
  | 'comparison'
  | 'multi-value'
  | 'channel'
  | 'country'
  | 'account'
  | 'account-summary'
  | 'yoy-mom';

export interface TemplateComparisonParams {
  param: string;
  placeholders: string[];
  base_value: string;
  fixed_params?: Record<string, string>;
}

export interface TemplateParameters {
  required: string[];
  implicit?: Record<string, string>;
  comparison?: TemplateComparisonParams;
}

export interface TemplateDefinition {
  id: string;
  template: string;
  description: string;
  parameters: TemplateParameters;
  weight: number;
  isComparison: boolean;
  /** Stable internal key for React reconciliation. Never changes once assigned. */
  _stableKey?: string;
}

export interface TemplatePattern extends TemplateDefinition {
  category: TemplateCategory;
  enabledByDefault: boolean;
}

export interface GroupExpansionEntry {
  label: string;
  expands_to: string[];
  weight: number;
  /** Stable internal key for React reconciliation. Never changes once assigned. */
  _stableKey?: string;
}

// ─── LLM Configuration ────────────────────────────────────────────────

export interface LLMModelConfig {
  model: string;
  temperature: number;
  max_tokens: number;
  region_name: string;
}

export interface LLMValidatorConfig extends LLMModelConfig {
  enabled: boolean;
}

export interface LLMConfig {
  polisher: LLMModelConfig;
  validator: LLMValidatorConfig;
}

// ─── Tool Configuration ────────────────────────────────────────────────

export interface ToolConfig {
  schema: string;
  temporal_parameter_name: string;
  evaluation_criteria: string;
  allowed_additional_tools: string[];
}

// ─── Sampling & Selection ──────────────────────────────────────────────

export interface MetricsSelection {
  coupled_with_other_params: boolean;
  enum: string[];
}

export interface DimensionsSelection {
  enabled: boolean;
  probability: number;
  min_values: number;
  max_values: number;
  enum: string[];
}

export interface CriteriaParam {
  enabled: boolean;
  default_criteria: string;
}

export interface ParameterOutputFormat {
  ignore_patterns: string[];
  criteria_params: Record<string, CriteriaParam>;
  apply_criteria_to_all_arrays: boolean;
}

export interface LLMPolish {
  removable_values: string[];
  must_preserve: string[];
}

export interface ColumnMapping {
  metrics: string;
  min_date: string;
  max_date: string;
}

export interface SelectionSettings {
  questions_per_template: number;
  diversity_enabled: boolean;
}

export interface TimePeriodGeneration {
  enable_months: boolean;
  enable_quarters: boolean;
  enable_half_years: boolean;
  enable_years: boolean;
  enable_ytd: boolean;
  enable_qtd: boolean;
}

// ─── Database Configuration ────────────────────────────────────────────

export interface SecretArnByEnvironment {
  test: string;
  beta: string;
  live: string;
}

export interface DatabaseConfig {
  type: string;
  cluster_identifier: string;
  database: string;
  stored_procedure: string;
  output_schema: string;
  source_table_name: string;
  base_metrics_catalog: string;
  derived_metrics_catalog: string;
  default_agg_columns: string;
  max_workers: number;
  secret_arn: SecretArnByEnvironment;
  default_environment: string;
  aws_profile: string;
}

// ─── Root Questions Config ─────────────────────────────────────────────

export interface RootQuestionsConfig {
  mode: string;
  v5_config: Record<string, unknown>;
}

// ─── Schema Derivation ─────────────────────────────────────────────────

/**
 * Result of schema derivation — all config values derivable from
 * structural analysis of a tool's inputSchema.
 */
export interface SchemaDerivationResult {
  tools: Record<string, ToolConfig>;
  templates: Record<string, Array<TemplateDefinition & { enabled: boolean }>>;
  metricsSelection: MetricsSelection;
  dimensionsSelection: DimensionsSelection;
  schemaRequiredDefaults: Record<string, unknown>;
  paramNameMapping: Record<string, string>;
  columnMapping: ColumnMapping;
  frequencyWeightColumns: string[];
  groupExpansion: Record<string, GroupExpansionEntry[]>;
  llmPolish: LLMPolish;
  stratifyColumns: string[];
}

// ─── Config Page State ─────────────────────────────────────────────────

export interface ConfigPageState {
  // Tool Selection
  selectedToolId: string | null;
  selectedRevisionTrack: 'live' | 'beta' | 'test';
  selectedRevision: ToolRevisionData | null;
  schemaSummary: SchemaSummary | null;
  schemaContent: string;

  // User Inputs
  businessKnowledge: string;
  rootQuestions: string;

  // Pre-population state
  isPrePopulated: boolean;

  // LLM Configuration
  llm: LLMConfig;

  // Tool Configuration
  tools: Record<string, ToolConfig>;

  // Jargon Prompt
  jargonPrompt: string;

  // Templates (pattern library with enable/disable)
  templates: Record<string, Array<TemplateDefinition & { enabled: boolean }>>;

  // Group Expansions
  groupExpansion: Record<string, GroupExpansionEntry[]>;

  // Sampling & Selection
  metricsSelection: MetricsSelection;
  dimensionsSelection: DimensionsSelection;
  parameterOutputFormat: ParameterOutputFormat;
  schemaRequiredDefaults: Record<string, unknown>;
  paramNameMapping: Record<string, string>;
  llmPolish: LLMPolish;

  // Column Mapping
  columnMapping: ColumnMapping;

  // Frequency Weight Columns
  frequencyWeightColumns: string[];

  // OrderBy Selection
  orderBySelection: { enabled: boolean; probability: number };

  // Stratified Sampling
  stratifiedSampling: {
    enabled: boolean;
    frequency_weight_columns: string[];
    stratify_columns: string[];
    exclude_columns: string[];
  };

  // Group Expansion top-level controls
  groupExpansionEnabled: boolean;
  groupExpansionProbability: number;

  // Selection Settings
  selectionSettings: SelectionSettings;

  // Time Period Generation
  timePeriodGeneration: TimePeriodGeneration;

  // Database Configuration
  databaseConfig: DatabaseConfig;

  // Root Questions Config
  rootQuestionsConfig: RootQuestionsConfig;

  // Output
  generatedYaml: string;
  suggestedFilename: string;
  s3TargetPath: string;
}

// ─── Config Page Actions ───────────────────────────────────────────────

export type ConfigPageAction =
  // Tool Selection
  | { type: 'SELECT_TOOL'; toolId: string }
  | { type: 'SET_REVISION_TRACK'; track: 'live' | 'beta' | 'test' }
  | {
      type: 'SET_REVISION_DATA';
      revision: ToolRevisionData;
      summary: SchemaSummary;
      schemaContent: string;
    }

  // User Inputs
  | { type: 'SET_BUSINESS_KNOWLEDGE'; value: string }
  | { type: 'SET_ROOT_QUESTIONS'; value: string }

  // Pre-population
  | { type: 'POPULATE_FROM_RESPONSE'; response: PrePopulatedConfigResponse }
  | { type: 'RESET_PREPOPULATION' }

  // Schema Derivation
  | { type: 'APPLY_SCHEMA_DERIVATION'; payload: SchemaDerivationResult }

  // Generic field update (dot-path)
  | { type: 'UPDATE_FIELD'; path: string; value: unknown }

  // Template actions
  | { type: 'TOGGLE_TEMPLATE'; toolName: string; stableKey: string }
  | {
      type: 'UPDATE_TEMPLATE';
      toolName: string;
      stableKey: string;
      field: string;
      value: unknown;
    }
  | {
      type: 'ADD_TEMPLATE';
      toolName: string;
      template: TemplateDefinition & { enabled: boolean };
    }
  | { type: 'DUPLICATE_TEMPLATE'; toolName: string; stableKey: string }
  | { type: 'REMOVE_TEMPLATE'; toolName: string; stableKey: string }

  // Group expansion actions
  | { type: 'ADD_GROUP_EXPANSION'; paramName: string; entry: GroupExpansionEntry }
  | { type: 'REMOVE_GROUP_EXPANSION'; paramName: string; index: number }
  | {
      type: 'UPDATE_GROUP_EXPANSION';
      paramName: string;
      index: number;
      field: string;
      value: unknown;
    }

  // Output
  | { type: 'SET_GENERATED_YAML'; yaml: string; filename: string }
  | { type: 'SET_S3_TARGET_PATH'; path: string };

// ─── Validation ────────────────────────────────────────────────────────

export type ValidationErrors = Record<string, string>;

// ─── API Request / Response Interfaces ─────────────────────────────────

export interface PrePopulateRequest {
  schema_content: string;
  schema_filename: string;
  business_knowledge: string;
  root_questions: string;
}

export interface PrePopulatedConfigResponse {
  jargon_prompt: string;
  templates: Record<string, Array<TemplateDefinition & { enabled?: boolean }>>;
  group_expansion: Record<string, GroupExpansionEntry[]>;
}

export interface GenerateConfigRequest {
  llm: LLMConfig;
  tools: Record<string, ToolConfig>;
  root_questions: RootQuestionsConfig;
  database: DatabaseConfig;
  orderBy_selection?: { enabled: boolean; probability: number };
  stratified_sampling?: {
    enabled: boolean;
    frequency_weight_columns: string[];
    stratify_columns: string[];
    exclude_columns: string[];
  };
  group_expansion_settings?: { enabled: boolean; probability: number };
}

export interface GenerateConfigResponse {
  yaml_content: string;
  filename: string;
}

export interface SaveConfigRequest {
  yaml_content: string;
  target_path: string;
}

export interface SaveConfigResponse {
  saved_path: string;
  message: string;
}

// ─── API Error ─────────────────────────────────────────────────────────

export class ConfigGeneratorApiError extends Error {
  public readonly statusCode: number;
  public readonly detail: string;

  constructor(statusCode: number, detail: string) {
    super(`API error ${statusCode}: ${detail}`);
    this.name = 'ConfigGeneratorApiError';
    this.statusCode = statusCode;
    this.detail = detail;
  }
}
